import { lazy, Suspense, useEffect, useState } from 'react';
import { Button, Modal, Segmented, Space, Spin, theme } from 'antd';
import { isPartModel3d, type PartModel3d } from './types';

// three + @react-three/* is a large vendor chunk that only a handful of screens
// need, so the viewer loads the first time a model is actually opened.
const loadViewer = () => import('./PartModelViewer');
const PartModelViewer = lazy(loadViewer);

interface Props {
	open: boolean;
	/** URL of the line item's tessellated `file_3djs` model. */
	url: string | null;
	/** Shown in the modal title so the user knows which part they opened. */
	partName?: string | null;
	onClose: () => void;
}

/**
 * Full-part 3D preview. Geometry can run to several megabytes, so it is
 * fetched when the modal opens rather than alongside the line item.
 */
export default function PartModelModal({ open, url, partName, onClose }: Props) {
	const { token } = theme.useToken();
	const [model, setModel] = useState<PartModel3d['model'] | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [resetKey, setResetKey] = useState(0);
	const [xray, setXray] = useState(false);

	useEffect(() => {
		if (!open || !url) return;

		let cancelled = false;
		setLoading(true);
		setError(null);
		setModel(null);

		// Pull the viewer chunk down alongside the geometry rather than waiting
		// for the fetch to resolve and mount it.
		void loadViewer();

		fetch(url)
			.then((response) => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.json();
			})
			.then((data: unknown) => {
				if (cancelled) return;
				if (!isPartModel3d(data)) {
					setError('This part has no 3D model.');
					return;
				}
				setModel(data.model);
			})
			.catch(() => {
				if (!cancelled) setError('Could not load the 3D model for this part.');
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, url]);

	return (
		<Modal
			title={partName ? `3D Model — ${partName}` : '3D Model'}
			open={open}
			onCancel={onClose}
			centered
			destroyOnClose
			width="min(960px, calc(100vw - 80px))"
			styles={{
				// Zeroing the content padding is what lets the canvas reach the modal
				// edges; the header and footer have to put their own padding back.
				content: {
					display: 'flex',
					flexDirection: 'column',
					height: 'min(960px, calc(100vh - 32px))',
					padding: 0,
				},
				header: {
					padding: '12px 16px',
					margin: 0,
					borderBottom: `1px solid ${token.colorBorderSecondary}`,
				},
				body: {
					flex: '1 1 auto',
					minHeight: 0,
					padding: 0,
					overflow: 'hidden',
				},
				footer: {
					padding: '10px 16px',
					margin: 0,
					borderTop: `1px solid ${token.colorBorderSecondary}`,
				},
			}}
			footer={
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 12,
					}}
				>
					<Segmented
						value={xray ? 'X-Ray' : 'Shaded'}
						onChange={(value) => setXray(value === 'X-Ray')}
						options={['Shaded', 'X-Ray']}
						disabled={!model}
					/>
					<Space>
						<Button onClick={() => setResetKey((key) => key + 1)} disabled={!model}>
							Reset View
						</Button>
						<Button type="primary" onClick={onClose}>
							Close
						</Button>
					</Space>
				</div>
			}
		>
			<div
				style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				{loading && (
					<Space direction="vertical" align="center">
						<Spin size="large" />
						<span style={{ color: token.colorTextSecondary, fontSize: 13 }}>
							Loading 3D model…
						</span>
					</Space>
				)}
				{!loading && error && (
					<span style={{ color: token.colorTextSecondary, fontSize: 14 }}>{error}</span>
				)}
				{!loading && !error && model && (
					<Suspense fallback={<Spin size="large" />}>
						<PartModelViewer model={model} resetKey={resetKey} xray={xray} />
					</Suspense>
				)}
			</div>
		</Modal>
	);
}
